﻿import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { AuthProvider, useAuth } from "react-oidc-context";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import theme from "./theme/theme";
import { oidcConfig } from "./auth/config";
import { setAuthUserProvider } from "./api/client";
import ProtectedRoute from "./auth/ProtectedRoute";
import MainLayout from "./layouts/MainLayout";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { AppSettingsProvider } from "./contexts/AppSettingsContext";
import { BreadcrumbProvider } from "./contexts/BreadcrumbContext";
const DashboardPage = lazy(() => import("./pages/Dashboard/DashboardPage"));
const DevelopersPage = lazy(() => import("./pages/Developers/DevelopersPage"));
const DeveloperDetailPage = lazy(
  () => import("./pages/Developers/DeveloperDetailPage"),
);
const MergeRequestsPage = lazy(
  () => import("./pages/MergeRequests/MergeRequestsPage"),
);
const DevelopmentPage = lazy(
  () => import("./pages/Development/DevelopmentPage"),
);
const TicketDetailPage = lazy(
  () => import("./pages/Development/TicketDetailPage"),
);
const ProjectsPage = lazy(() => import("./pages/Projects/ProjectsPage"));
const SettingsPage = lazy(() => import("./pages/Settings/SettingsPage"));
const OperationsPage = lazy(
  () => import("./pages/Operations/OperationsPage"),
);
const SystemHealthPage = lazy(
  () => import("./pages/SystemHealth/SystemHealthPage"),
);
const QualityDashboardPage = lazy(
  () => import("./pages/Quality/QualityDashboardPage"),
);
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
function AppErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        p: 3,
      }}
    >
      <Typography variant="h5" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {error instanceof Error ? error.message : "An unexpected error occurred"}
      </Typography>
      <Button variant="contained" onClick={resetErrorBoundary}>
        Try again
      </Button>
    </Box>
  );
}
function RouteErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
        p: 4,
      }}
    >
      <Typography variant="h6" gutterBottom>
        This page encountered an error
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, textAlign: "center" }}>
        {error instanceof Error ? error.message : "An unexpected error occurred while loading this page."}
      </Typography>
      <Button variant="contained" onClick={resetErrorBoundary}>
        Try Again
      </Button>
    </Box>
  );
}
function AuthBridge({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  useEffect(() => {
    setAuthUserProvider(() => auth.user);
  }, [auth.user]);
  return <>{children}</>;
}
function withRouteBoundary(element: React.ReactNode) {
  return (
    <ErrorBoundary FallbackComponent={RouteErrorFallback}>
      {element}
    </ErrorBoundary>
  );
}
export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary FallbackComponent={AppErrorFallback}>
        <AuthProvider {...oidcConfig}>
          <AuthBridge>
            <SnackbarProvider>
              <AppSettingsProvider>
              <BreadcrumbProvider>
              <ProtectedRoute>
                <BrowserRouter>
                  <Suspense
                    fallback={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100vh",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    }
                  >
                    <Routes>
                      <Route element={<MainLayout />}>
                        <Route path="/" element={withRouteBoundary(<DashboardPage />)} />
                        <Route path="/developers" element={withRouteBoundary(<DevelopersPage />)} />
                        <Route path="/developers/:id" element={withRouteBoundary(<DeveloperDetailPage />)} />
                        <Route path="/merge-requests" element={withRouteBoundary(<MergeRequestsPage />)} />
                        <Route path="/development" element={withRouteBoundary(<DevelopmentPage />)} />
                        <Route path="/development/:id" element={withRouteBoundary(<TicketDetailPage />)} />
                        <Route path="/projects" element={withRouteBoundary(<ProjectsPage />)} />
                        <Route path="/guidelines" element={<Navigate to="/projects" replace />} />
                        <Route path="/quality" element={withRouteBoundary(<QualityDashboardPage />)} />
                        <Route path="/operations" element={withRouteBoundary(<OperationsPage />)} />
                        <Route path="/system-health" element={withRouteBoundary(<SystemHealthPage />)} />
                        <Route path="/settings" element={withRouteBoundary(<SettingsPage />)} />
                        <Route path="/reviews" element={<Navigate to="/merge-requests" replace />} />
                        <Route path="*" element={withRouteBoundary(<NotFoundPage />)} />
                      </Route>
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </ProtectedRoute>
              </BreadcrumbProvider>
              </AppSettingsProvider>
            </SnackbarProvider>
          </AuthBridge>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
